import type { ReactElement } from 'react';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';

interface BlockToggleProps {
  /** effectiveBlocked for the row/tool being rendered. */
  blocked: boolean;
  /**
   * Name of the server/tool this switch controls, used to give each switch
   * a distinct accessible name in a table with many rows.
   */
  name: string;
  /** Allowed -> instant, no confirmation. */
  onAllow: () => void;
  /**
   * Blocked -> opens the confirmation modal (KIR-3940); this callback only
   * requests it, it does not itself flip state.
   */
  onRequestBlock: () => void;
  disabled?: boolean;
  /** Extra classes for the switch itself (e.g. its checked colour). */
  switchClassName?: string;
}

/**
 * Shared Allowed/Blocked switch used for both the server-level
 * gateway-control cell and the per-tool row in the expansion (KIR-3940).
 * Controlled: it reflects `blocked` from data and never holds local
 * optimistic state, so a cancelled block leaves it exactly as it was.
 */
export function BlockToggle({
  blocked,
  name,
  onAllow,
  onRequestBlock,
  disabled,
  switchClassName,
}: BlockToggleProps): ReactElement {
  return (
    // The gateway-control cell sits on a row with click-to-expand, so without
    // this the switch would toggle the policy *and* expand/collapse the row.
    <div
      className="flex items-center gap-2"
      onClick={event => event.stopPropagation()}
    >
      <Switch
        checked={!blocked}
        onCheckedChange={checked => (checked ? onAllow() : onRequestBlock())}
        disabled={disabled}
        className={switchClassName}
        aria-label={`${blocked ? 'Allow' : 'Block'} ${name}`}
      />
      <span
        className={cn(
          'text-xs font-medium',
          blocked ? 'text-red-600' : 'text-green-600'
        )}
      >
        {blocked ? 'Blocked' : 'Allowed'}
      </span>
    </div>
  );
}
